"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { LastTrackerPosition, Vehicle } from "@/interfaces";
import type { LocationData } from "@/services/api/lastTrackerPosition";
import { format } from "date-fns";
import {
  CircleGauge,
  Clock,
  Gauge,
  Loader2,
  MapPin,
  Power,
  ScanEye,
  Timer,
  X,
} from "lucide-react";
import type React from "react";

interface SidebarProps {
  addressInfo?: LocationData;
  vehicle: Vehicle;
  lastPosition: LastTrackerPosition;
  onClose: () => void;
  loading: boolean;
}

const TruckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 sm:w-6 sm:h-6"
  >
    <path d="M10 17h4V5H2v12h3" />
    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
    <circle cx="7.5" cy="17.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

const CarIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 sm:w-6 sm:h-6"
  >
    <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" />
    <circle cx="6.5" cy="16.5" r="2.5" />
    <circle cx="16.5" cy="16.5" r="2.5" />
  </svg>
);

const VanIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 sm:w-6 sm:h-6"
  >
    <path d="M10 17h4V5H2v12h3" />
    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
    <circle cx="7.5" cy="17.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

const MotorcycleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 sm:w-6 sm:h-6"
  >
    <circle cx="5.5" cy="17.5" r="3.5" />
    <circle cx="18.5" cy="17.5" r="3.5" />
    <path d="M15 6h3l2 6" />
    <path d="M10 6h3l2 6" />
    <path d="M5 9h1.5l2 6" />
  </svg>
);

const BusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 sm:w-6 sm:h-6"
  >
    <path d="M19 17h2l.64-2.54c.24-.959.24-1.962 0-2.92l-1.07-4.27A3 3 0 0 0 17.66 5H4a2 2 0 0 0-2 2v10h2" />
    <path d="M14 17H9" />
    <circle cx="6.5" cy="17.5" r="2.5" />
    <circle cx="16.5" cy="17.5" r="2.5" />
  </svg>
);

const DefaultIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5 sm:w-6 sm:h-6"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <path d="M3 9h18" />
  </svg>
);

const getVehicleIcon = (vehicleType: string) => {
  switch (vehicleType.toLowerCase()) {
    case "truck":
      return <TruckIcon />;
    case "car":
      return <CarIcon />;
    case "van":
      return <VanIcon />;
    case "motorcycle":
      return <MotorcycleIcon />;
    case "bus":
      return <BusIcon />;
    default:
      return <DefaultIcon />;
  }
};

const getPrimaryAddress = (
  loading: boolean,
  addressInfo?: LocationData,
): React.ReactNode => {
  if (loading) {
    return (
      <div className="flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Carregando endereço...</span>
      </div>
    );
  }

  if (!addressInfo) {
    return "Não foi possível buscar o endereço";
  }

  if (!addressInfo.primary_address || addressInfo.primary_address === "") {
    return "Endereço não disponível";
  }

  return addressInfo.primary_address;
};

const fullAddress = (
  loading: boolean,
  addressInfo?: LocationData,
): React.ReactNode => {
  if (loading) {
    return (
      <div className="flex items-center gap-1">
        <Loader2 className="h-2 w-2 animate-spin" />
        <span>Carregando detalhes...</span>
      </div>
    );
  }

  if (!addressInfo) {
    return "Não foi possível buscar os detalhes do endereço";
  }

  if (!addressInfo.display_name || addressInfo.display_name === "") {
    return "Detalhes do endereço não disponíveis";
  }

  return addressInfo.display_name;
};

const getStatusColor = (ignition: number) => {
  return ignition > 0 ? "bg-green-500" : "bg-red-500";
};

const Sidebar: React.FC<SidebarProps> = ({
  addressInfo,
  vehicle,
  lastPosition,
  onClose,
  loading,
}) => {
  const statusColor = getStatusColor(lastPosition?.ignition);

  const formattedDate = format(
    new Date(lastPosition.date),
    "dd/MM/yyyy HH:mm:ss",
  );

  return (
    <div className="absolute left-8 top-8 z-1000 w-[300px]">
      <Card className="bg-white dark:bg-slate-900 shadow-lg border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
        <div className="bg-gray-50 dark:bg-slate-800 px-2 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2">
            <div className={`p-1 sm:p-1.5 rounded-full ${statusColor}`}>
              {getVehicleIcon(vehicle.vehicle_type_name ?? "")}
            </div>
            <div>
              <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate max-w-[120px] sm:max-w-[180px] md:max-w-[220px]">
                {vehicle.label || "Veículo"}
              </h3>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {formattedDate}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Power
              className={`${lastPosition.ignition ? "text-green-500" : "text-red-500"}`}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 h-7 w-7 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <CardContent className="p-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-b border-gray-200 dark:border-gray-700">
            <div className="p-3 sm:p-4 sm:border-r border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1 sm:gap-2 mb-1">
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                <span className="text-xs sm:text-sm font-medium">Endereço</span>
              </div>

              {loading ? (
                <>
                  <Skeleton className="h-3 sm:h-4 w-full mb-1 sm:mb-2" />
                  <Skeleton className="h-2 sm:h-3 w-3/4" />
                </>
              ) : (
                <>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 wrap-break-word">
                    {getPrimaryAddress(loading, addressInfo)}
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
                    {fullAddress(loading, addressInfo)}
                  </p>
                </>
              )}
            </div>

            <div className="p-3 sm:p-4 border-t sm:border-t-0 border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-1 sm:gap-2 mb-1">
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                <span className="text-xs sm:text-sm font-medium">
                  Última Atualização
                </span>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                {formattedDate}
              </p>
            </div>
            {lastPosition.odometer != null && (
              <div className="p-3 sm:p-4 border-r border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 sm:gap-2 mb-1">
                  <CircleGauge className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                  <span className="text-xs sm:text-sm font-medium">
                    Odômetro
                  </span>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {lastPosition.odometer
                    ? lastPosition.odometer.toLocaleString()
                    : ""}
                </p>
              </div>
            )}

            {vehicle.has_engine_on_info && (
              <div className="p-3 sm:p-4 border-r border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 sm:gap-2 mb-1">
                  <Timer className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                  <span className="text-xs sm:text-sm font-medium">
                    Horímetro
                  </span>
                </div>
                <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {lastPosition.hour_meter != null
                    ? `${lastPosition.hour_meter.toLocaleString()} h`
                    : ""}
                </p>
              </div>
            )}

            <div className="p-3 sm:p-4">
              <div className="flex items-center gap-1 sm:gap-2 mb-1">
                <Gauge className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
                <span className="text-xs sm:text-sm font-medium">
                  Velocidade
                </span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-200">
                {lastPosition.speed} km/h
              </p>
            </div>
          </div>

          {(lastPosition.is_trunk_open !== null ||
            lastPosition.is_door_open !== null ||
            lastPosition.is_lock_open !== null ||
            vehicle.inside_safe_zone) && (
            <>
              <div className="p-3 sm:p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="space-y-1 sm:space-y-2">
                  {vehicle.inside_safe_zone && (
                    <div className="flex items-center text-[10px] sm:text-xs text-green-600">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500 mr-1 sm:mr-2"></div>
                      Dentro da zona segura
                    </div>
                  )}

                  {lastPosition.is_trunk_open !== null && (
                    <div className="flex items-center text-[10px] sm:text-xs">
                      <div
                        className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${lastPosition.is_trunk_open === 1 ? "bg-yellow-500" : "bg-green-500"} mr-1 sm:mr-2`}
                      ></div>
                      Baú{" "}
                      {lastPosition.is_trunk_open === 1 ? "aberto" : "fechado"}
                    </div>
                  )}

                  {lastPosition.is_door_open !== null && (
                    <div className="flex items-center text-[10px] sm:text-xs">
                      <div
                        className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${lastPosition.is_door_open === 1 ? "bg-yellow-500" : "bg-green-500"} mr-1 sm:mr-2`}
                      ></div>
                      Porta{" "}
                      {lastPosition.is_door_open === 1 ? "aberta" : "fechada"}
                    </div>
                  )}

                  {lastPosition.is_lock_open !== null && (
                    <div className="flex items-center text-[10px] sm:text-xs">
                      <div
                        className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${lastPosition.is_lock_open === 1 ? "bg-yellow-500" : "bg-green-500"} mr-1 sm:mr-2`}
                      ></div>
                      Trava{" "}
                      {lastPosition.is_lock_open === 1
                        ? "desacionada"
                        : "acionada"}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="p-3 sm:p-4 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs h-8 bg-white hover:bg-gray-50 border-gray-200 text-gray-700"
              disabled={loading}
              onClick={() => {
                const streetViewUrl = `https://www.google.com/maps?q=&layer=c&cbll=${lastPosition.latitude},${lastPosition.longitude}`;
                window.open(streetViewUrl, "_blank");
              }}
            >
              <ScanEye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> GOOGLE STREET
              VIEW
            </Button>
          </div>
          {loading && (
            <div className="p-3 sm:p-4 bg-blue-50 border-t border-blue-200 flex items-center justify-center">
              <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 animate-spin mr-1 sm:mr-2" />
              <span className="text-[10px] sm:text-sm text-blue-700">
                Carregando informações do endereço...
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Sidebar;
